import type { DisplayWord } from "@circleofa/acim-engine"
import type { HighlightID } from "@store/highlights"
import type { FocusExpander, SquintEventEmitter } from "./focusser"

export enum HighlightSettingLimiter {
    NONE,
    ALL,
    /**
     * Only able to edit the emphasized highlight
     */
    EMPHASIZED,
}

export enum FocusSetting {
    HIGHLIGHT,
    WORDS,
}

export type PopWords = [start: DisplayWord, end: DisplayWord][]

/**
 * This essentially tells the {@link globalSelectHandler} to use
 * a different technique. Instead of using the {@link contextScrollContainerClass}
 * and placing the context menu and grabbers in there, it dynamically
 * calculates every frame using {@link requestAnimationFrame} with every scroll
 * attempt. This is used to "break out" of the bounds of the scroll container.
 * It's not as desirable because it comes at a performance cost.
 */
export enum ContextMenuViewportSelection {
    SCROLL_CONTAINER,
    VIEWPORT,
}

export type HighlighterListenerSettings = {
    /**
     * Allowed to select at all
     * @default true
     */
    selection: boolean

    /**
     * Toggle the ability to create or remove highlights
     * @default true
     */
    createHighlights: boolean

    /**
     * Edit highlights
     * @default none {@link HighlightSettingLimiter.ALL}
     */
    editHighlights: HighlightSettingLimiter

    /**
     * Denote one highlight as the emphasized highlight
     * Use "faded" to fade all highlights, this is just a simple
     * little hack because no highlight IDs match 'faded'
     * @default null
     */
    emphasizeHighlight: HighlightID | null | "faded"

    /**
     * Show highlights
     * @default none {@link HighlightSettingLimiter.ALL}
     */
    showHighlights: HighlightSettingLimiter

    /**
     * Highlight certain words, like in a search result
     * @default null
     */
    popWords: PopWords | null

    /**
     * Flash the highlight instead of persisting it
     * @default false
     */
    popWordsFlash: boolean

    /**
     * Slices the reader's top and bottom to 'focus in'
     * on a certain item. Must also set {@link squintEventEmitter}
     * @default null
     */
    focusOn: FocusSetting | null

    /**
     * Emits the events to slice the focus, goes hand-in-hand
     * with {@link focusOn}
     */
    squintEventEmitter: SquintEventEmitter | null

    /**
     * Use viewport selection instead of scroll container.
     * See {@link ContextMenuViewportSelection}
     * @default ContextMenuViewportSelection.SCROLL_CONTAINER
     */
    viewportSelection: ContextMenuViewportSelection

    /**
     * Callbacks to different needed. Not implemented yet. WIP
     * @default - {@link defaultCallbacks} just some void functions
     */
    callbacks: Partial<{
        loading: () => unknown
        painted: () => unknown
        selected: () => unknown
        deselected: () => unknown
    }>

    /**
     * Force instant paint.
     * Skips the timeout for painting.
     */
    forceInstantFirstPaint: boolean

    /**
     * Focus expander allows us to trigger single element expanding
     * and contracting events for revealing unfocussed areas
     * @default null
     */
    focusExpander: FocusExpander | null
}

const defaultCallbacks = {
    loading: () => {
        /** */
    },
    painted: () => {
        /** */
    },
    selected: () => {
        /** */
    },
    deselected: () => {
        /** */
    },
} as const
Object.seal(defaultCallbacks)

export const HighlighterListenerSettingsDefault: HighlighterListenerSettings = {
    selection: true,
    createHighlights: true,
    editHighlights: HighlightSettingLimiter.ALL,
    emphasizeHighlight: null,
    showHighlights: HighlightSettingLimiter.ALL,
    popWords: null,
    popWordsFlash: false,
    focusOn: null,
    squintEventEmitter: null,
    viewportSelection: ContextMenuViewportSelection.SCROLL_CONTAINER,
    forceInstantFirstPaint: false,
    callbacks: defaultCallbacks,
    focusExpander: null,
} as const
Object.seal(HighlighterListenerSettingsDefault)
